import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, BookOpen, MapPin, User, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getCourseMetadata } from '../config/courses';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  icon: React.ComponentType<{ size: number }>;
  label: string;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Detect if we're inside a course view
  const courseMatch = location.pathname.match(/^\/course\/([^/]+)/);
  const currentCourseId = courseMatch ? courseMatch[1] : null;
  const courseMeta = currentCourseId ? getCourseMetadata(currentCourseId) : null;
  const isInCourse = !!currentCourseId;

  // Build context-dependent navigation items
  const navItems: NavItem[] = useMemo(() => {
    if (isInCourse && currentCourseId) {
      // Inside a course: Home, Kurs, (Karte if hasMap), Profil
      const items: NavItem[] = [
        { path: '/', icon: Home, label: 'Home' },
        { path: `/course/${currentCourseId}`, icon: BookOpen, label: 'Kurs' },
      ];

      // Only show map tab if course has a map
      if (courseMeta?.hasMap) {
        items.push({ path: '/map', icon: MapPin, label: 'Karte' });
      }

      items.push({ path: '/profile', icon: User, label: 'Profil' });
      return items;
    } else {
      // Dashboard / Profile / other top-level: only Home and Profil
      return [
        { path: '/', icon: Home, label: 'Home' },
        { path: '/profile', icon: User, label: 'Profil' },
      ];
    }
  }, [isInCourse, currentCourseId, courseMeta?.hasMap]);

  // Check if path is active (handle course subpaths)
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path.startsWith('/course/')) {
      // Kurs tab active for any course subpath
      return location.pathname.startsWith(path.replace(/\/$/, ''));
    }
    return location.pathname === path;
  };

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="header-brand">
          <span className="header-emoji">🎓</span>
          <span className="header-title">Adrianas Lernwelt</span>
        </div>
        {user && (
          <div className="header-user">
            <span className="user-name">{user.name}</span>
            <button className="logout-btn" onClick={logout} title="Abmelden">
              <LogOut size={18} />
            </button>
          </div>
        )}
      </header>

      <main className="app-main">
        {children}
      </main>

      <nav className="app-nav no-print" style={{
        justifyContent: navItems.length <= 2 ? 'center' : 'space-around',
        gap: navItems.length <= 2 ? '48px' : '0',
      }}>
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`nav-item ${isActive(path) ? 'active' : ''}`}
            style={{
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
          >
            <Icon size={22} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
